import { getCurrentInstance, onMounted, ref, type Ref } from 'vue'

import ClubProfileRequestService from '@/services/clubs/clubProfileRequestService'
import { handleRequest } from '@/utils/handleRequest';

interface CacheEntry {
  clubProfileRequest: Ref<any>
  loaded: boolean
  inFlight: Promise<any> | null
}

// Shared per cprId so the show route guard's fetch is reused by the mounted
// view instead of refetching the same record.
const cache = new Map<string, CacheEntry>()

function entryFor(cprId: string): CacheEntry {
  let entry = cache.get(cprId)
  if (!entry) {
    entry = { clubProfileRequest: ref(null), loaded: false, inFlight: null }
    cache.set(cprId, entry)
  }
  return entry
}

// Route-guard policy for a single club profile request. The show endpoint
// itself calls `authorize @club_profile_request, :show?`, so a fetch failure
// (403) already reflects the access decision — the guard only needs to check
// whether the fetch succeeded.
export function useClubProfileRequestRoutePolicy(clubId: string | number, cprId: string | number) {

  const entry = entryFor(String(cprId))
  const clubProfileRequest = entry.clubProfileRequest

  const fetchClubProfileRequest = async ({ force = true } = {}) => {
    if (!force && entry.loaded) return { success: true, data: clubProfileRequest.value }
    if (entry.inFlight) return await entry.inFlight

    entry.inFlight = handleRequest(
      () => ClubProfileRequestService.getRequest(clubId, cprId),
      {
        onSuccess: (response: any) => {
          clubProfileRequest.value = response.data
          entry.loaded = true
        }
      }
    )

    try {
      return await entry.inFlight
    } finally {
      entry.inFlight = null
    }
  }

  const canShow = () => entry.loaded

  // Auto-fetch only when used inside a component; route guards call
  // fetchClubProfileRequest() explicitly and have no instance to mount against.
  if (getCurrentInstance()) {
    onMounted(async () => await fetchClubProfileRequest({ force: false }))
  }

  return {
    clubProfileRequest,
    fetchClubProfileRequest,
    canShow
  }
}
